import React from 'react'
import { cn } from '../../lib/utils'
import type { CardVariant, BaseComponentProps } from '../../types'

export interface CardProps extends BaseComponentProps {
  variant?: CardVariant
  hover?: boolean
  gradient?: 'primary' | 'secondary' | 'accent'
  padding?: 'none' | 'sm' | 'md' | 'lg'
}

const variantClasses: Record<CardVariant, string> = {
  default: 'card',
  bordered: 'card card-bordered',
  elevated: 'card card-elevated',
  gradient: 'text-white',
  glass: 'bg-white/10 backdrop-blur-lg border border-white/20',
}

const gradientClasses = {
  primary: 'gradient-bg-primary',
  secondary: 'gradient-bg-secondary',
  accent: 'gradient-bg-accent',
}

const paddingClasses = {
  none: 'p-0',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      variant = 'default',
      hover = false,
      gradient,
      padding = 'md',
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          variantClasses[variant],
          hover && 'card-hover',
          gradient && gradientClasses[gradient],
          paddingClasses[padding],
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Card.displayName = 'Card'

export const CardHeader: React.FC<BaseComponentProps> = ({ children, className, ...props }) => (
  <div className={cn('mb-4', className)} {...props}>
    {children}
  </div>
)

export const CardContent: React.FC<BaseComponentProps> = ({ children, className, ...props }) => (
  <div className={cn('', className)} {...props}>
    {children}
  </div>
)

export const CardFooter: React.FC<BaseComponentProps> = ({ children, className, ...props }) => (
  <div className={cn('mt-4 pt-4 border-t border-border', className)} {...props}>
    {children}
  </div>
)
